import SaveIcon from '@mui/icons-material/Save';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs, { Dayjs } from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import ProfileHeader from './ProfileHeader';
import HeaderManager from '../../components/headerManager';
import SidebarResponsiveComp from '../../components/sideBar/siderbar-responsive';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/Toast';
import { isSupportedLocale, LOCALE_METADATA } from '../../i18n/config';
import { patchMe } from '../../services/user/user';
import { getCountryOptions, resolveCountryCode } from '../../i18n/country';
import { birthDatePickerCalendarSx, getBirthDateFormat } from '../../i18n/birthDate';
import { useAppLocale } from '../../i18n/useAppLocale';
import LocaleFlag from '../../components/localeFlag';
import {
  buildProfileChanges,
  isValidProfileBirthDate,
  isValidProfileSex,
  normalizeProfileFormValues,
  persistProfileChanges,
  ProfileFormValues,
  ProfileSex,
} from './profileForm';

interface ProfileProps {
  isAdminProfile?: boolean;
}

const adminProfileInputSx = {
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: '#c9c9c9',
    borderRadius: '6px',
    boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.05)',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: '#c9c9c9',
  },
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: '#c9c9c9',
  },
  '& .MuiOutlinedInput-root.Mui-disabled': {
    backgroundColor: '#f0f0f0',
    color: '#a0a0a0',
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: '#c9c9c9',
    },
  },
};

const darkProfileInputSx = {
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#2A2A48',
    borderRadius: '10px',
    color: '#FFF',
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(255, 255, 255, 0.35)',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(255, 255, 255, 0.35)',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(255, 255, 255, 0.35)',
    },
    '&.Mui-disabled': {
      backgroundColor: '#2A2A48',
      color: '#A0A0A0',
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: 'rgba(255, 255, 255, 0.35)',
      },
      '& .MuiOutlinedInput-input': {
        color: '#A0A0A0',
        WebkitTextFillColor: '#A0A0A0',
        opacity: 0.7,
      },
    },
  },
  '& .MuiOutlinedInput-input': {
    color: '#FFF',
  },
  '& .MuiInputLabel-root': {
    color: '#FFF',
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: '#FFF',
  },
  '& .MuiInputLabel-root.Mui-disabled': {
    color: '#A0A0A0',
    opacity: 0.7,
  },
  '& .MuiFormHelperText-root': {
    color: '#FFF',
    opacity: 0.7,
  },
  '& .MuiFormHelperText-root.Mui-disabled': {
    color: '#A0A0A0',
    opacity: 0.7,
  },
  '& .MuiSelect-icon': {
    color: '#FFF',
  },
  '& .MuiInputAdornment-root .MuiIconButton-root, & .MuiInputAdornment-root .MuiSvgIcon-root': {
    color: '#FFF',
  },
};

const blockedProfileInputSx = {
  '& .MuiOutlinedInput-root.Mui-disabled, & .MuiOutlinedInput-root.Mui-disabled *': {
    cursor: 'not-allowed',
  },
  '& .MuiFormHelperText-root': {
    marginLeft: 0,
  },
};

const profileActionButtonSx = {
  textTransform: 'none',
  borderRadius: '2em',
  padding: '0.5em 2em',
  fontSize: '1em',
  fontWeight: 500,
};

const Profile = ({ isAdminProfile = false }: ProfileProps) => {
  const { t } = useTranslation('profile');
  const appLocale = useAppLocale();
  const { user, updateCurrentUser } = useAuth();
  const { showToast } = useToast();
  const [form, setForm] = useState<ProfileFormValues>(() =>
    normalizeProfileFormValues(user?.user ?? {}),
  );
  const [initialForm, setInitialForm] = useState<ProfileFormValues>(() =>
    normalizeProfileFormValues(user?.user ?? {}),
  );
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!user?.user?.id) return;
    const nextForm = normalizeProfileFormValues(user.user);
    const localChanges = buildProfileChanges(initialForm, form);
    if (Object.keys(localChanges).length === 0) {
      setForm(nextForm);
      setInitialForm(nextForm);
      return;
    }

    const mergedForm = { ...form };
    const mergedInitial = { ...initialForm };
    (Object.keys(nextForm) as Array<keyof ProfileFormValues>).forEach((field) => {
      if (!(field in localChanges)) {
        (mergedForm as Record<keyof ProfileFormValues, string | number>)[field] = nextForm[field] as string | number;
        (mergedInitial as Record<keyof ProfileFormValues, string | number>)[field] = nextForm[field] as string | number;
      }
    });
    setForm(mergedForm);
    setInitialForm(mergedInitial);
  }, [
    user?.user?.id,
    user?.user?.first_name,
    user?.user?.last_name,
    user?.user?.country,
    user?.user?.birth_date,
    user?.user?.sex,
    user?.user?.locale,
  ]);

  const changes = useMemo(
    () => buildProfileChanges(initialForm, form),
    [initialForm, form],
  );
  const isDirty = Object.keys(changes).length > 0;

  const updateField = (field: keyof ProfileFormValues, value: string | number) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const handleBirthDateChange = (value: Dayjs | null) => {
    updateField('birth_date', value?.isValid() ? value.format('YYYY-MM-DD') : '');
  };

  const handleDiscard = () => {
    if (!isSaving) setForm(initialForm);
  };

  const handleSave = async () => {
    if (!user?.user?.id || !isDirty || isSaving) return;

    if (!form.first_name.trim() || !form.last_name.trim()) {
      showToast(t('validation.requiredName'), 'error');
      return;
    }
    if (!form.country.trim()) {
      showToast(t('validation.requiredCountry'), 'error');
      return;
    }
    if (!isValidProfileBirthDate(form.birth_date)) {
      showToast(t('validation.requiredBirthDate'), 'error');
      return;
    }
    if (!isValidProfileSex(form.sex)) {
      showToast(t('validation.invalidSex'), 'error');
      return;
    }
    if (!isSupportedLocale(form.locale)) {
      showToast(t('validation.invalidLocale'), 'error');
      return;
    }

    setIsSaving(true);
    try {
      const persisted = await persistProfileChanges(
        user.user.id,
        changes,
        patchMe,
        updateCurrentUser,
      );
      const nextForm = normalizeProfileFormValues({ ...initialForm, ...persisted });
      setForm(nextForm);
      setInitialForm(nextForm);
      showToast(t('updateSuccess', { lng: nextForm.locale }), 'success');
    } catch {
      showToast(t('updateError'), 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const readOnlyHelp = t('readOnlyAuthFieldHelp');

  return (
    <Box
      sx={{
        minHeight: 'calc(100vh - 6rem)',
        boxSizing: 'border-box',
        backgroundColor: isAdminProfile ? '#F5F7F8' : '#2A2A48',
        pb: { xs: 5, md: 7 },
      }}
    >
      {isAdminProfile ? (
        <>
          <SidebarResponsiveComp title={t('title')} />
          <HeaderManager title={t('title')} />
        </>
      ) : (
        <ProfileHeader />
      )}
      <Box sx={{ px: { xs: 2, sm: 3, md: 5 }, py: { xs: 3, md: 5 } }}>
        <Box sx={{ width: '100%', maxWidth: 980, mx: 'auto' }}>
          <Typography sx={{ color: isAdminProfile ? '#3C4043' : '#FFF', opacity: 0.75, mt: 1, mb: 3 }}>
            {t('subtitle')}
          </Typography>

          <Card
            elevation={4}
            sx={{
              borderRadius: { xs: 2, sm: 3 },
              overflow: 'visible',
              backgroundColor: isAdminProfile ? '#FFF' : '#373956',
            }}
          >
            <CardContent sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
              <Grid container spacing={2.5}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={t('fields.firstName')}
                    value={form.first_name}
                    onChange={(event) => updateField('first_name', event.target.value)}
                    disabled={isSaving}
                    sx={isAdminProfile ? adminProfileInputSx : darkProfileInputSx}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={t('fields.lastName')}
                    value={form.last_name}
                    onChange={(event) => updateField('last_name', event.target.value)}
                    disabled={isSaving}
                    sx={isAdminProfile ? adminProfileInputSx : darkProfileInputSx}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={t('fields.email')}
                    value={user?.user?.email ?? ''}
                    InputProps={{ readOnly: true }}
                    helperText={readOnlyHelp}
                    disabled
                    sx={{
                      ...(isAdminProfile ? adminProfileInputSx : darkProfileInputSx),
                      ...blockedProfileInputSx,
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={t('fields.cellphone')}
                    value={user?.user?.cellphone ?? ''}
                    InputProps={{ readOnly: true }}
                    helperText={readOnlyHelp}
                    disabled
                    sx={{
                      ...(isAdminProfile ? adminProfileInputSx : darkProfileInputSx),
                      ...blockedProfileInputSx,
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    select
                    label={t('fields.country')}
                    value={form.country}
                    onChange={(event) => updateField('country', event.target.value)}
                    disabled={isSaving}
                    sx={isAdminProfile ? adminProfileInputSx : darkProfileInputSx}
                  >
                    <MenuItem value="" disabled>{t('fields.selectOption')}</MenuItem>
                    {getCountryOptions(appLocale).map((country) => (
                      <MenuItem key={country.value} value={country.value}>
                        {country.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <DatePicker
                    label={t('fields.birthDate')}
                    value={form.birth_date ? dayjs(form.birth_date) : null}
                    onChange={handleBirthDateChange}
                    format={getBirthDateFormat(appLocale)}
                    disabled={isSaving}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        sx: isAdminProfile ? adminProfileInputSx : darkProfileInputSx,
                      },
                      layout: { sx: birthDatePickerCalendarSx },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    select
                    label={t('fields.sex')}
                    value={form.sex}
                    onChange={(event) => updateField('sex', Number(event.target.value) as ProfileSex)}
                    disabled={isSaving}
                    sx={isAdminProfile ? adminProfileInputSx : darkProfileInputSx}
                  >
                    <MenuItem value={0} disabled>{t('fields.selectOption')}</MenuItem>
                    <MenuItem value={1}>{t('sex.male')}</MenuItem>
                    <MenuItem value={2}>{t('sex.female')}</MenuItem>
                    <MenuItem value={3}>{t('sex.notInformed')}</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    select
                    label={t('fields.locale')}
                    value={form.locale}
                    onChange={(event) => updateField('locale', event.target.value)}
                    disabled={isSaving}
                    sx={isAdminProfile ? adminProfileInputSx : darkProfileInputSx}
                  >
                    {LOCALE_METADATA.map(({ code, label, flag }) => (
                      <MenuItem key={code} value={code}>
                        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                          <LocaleFlag src={flag} />
                          <span>{label}</span>
                        </Box>
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </Grid>

              <Stack
                direction={{ xs: 'column-reverse', sm: 'row' }}
                spacing={2}
                justifyContent="flex-end"
                sx={{ mt: 4 }}
              >
                <Button
                  variant="outlined"
                  onClick={handleDiscard}
                  disabled={!isDirty || isSaving}
                  sx={{
                    ...profileActionButtonSx,
                    ...(isAdminProfile ? {} : {
                      color: '#FFF',
                      borderColor: '#A0A0A0',
                      '&:hover': {
                        borderColor: '#FFF',
                        backgroundColor: 'rgba(255, 255, 255, 0.08)',
                      },
                      '&.Mui-disabled': {
                        color: 'rgba(255, 255, 255, 0.45)',
                        borderColor: 'rgba(255, 255, 255, 0.25)',
                      },
                    }),
                  }}
                >
                  {t('actions.discard')}
                </Button>
                <Button
                  variant="contained"
                  onClick={() => void handleSave()}
                  disabled={!isDirty || isSaving}
                  startIcon={isSaving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                  sx={{
                    ...profileActionButtonSx,
                    backgroundColor: '#2491FF',
                    color: '#FFF',
                    '&:hover': {
                      backgroundColor: '#2491FF',
                    },
                    '&.Mui-disabled': {
                      backgroundColor: '#2491FF',
                      color: '#FFF',
                      opacity: 0.5,
                    },
                  }}
                >
                  {t('actions.save')}
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Box>
      </Box>
    </Box>
  );
};

export default Profile;
